import { useEffect, useMemo, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import type { Control, UseFormSetValue } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Button,
  Description,
  FieldError,
  Header,
  Label,
  ListBox,
  ListBoxItem,
  Select,
} from '@heroui/react'

import { FormModal } from '@/components/ui/form-modal'
import { FormSection, FormSectionEmpty } from '@/components/ui/form-section'
import {
  NumberFieldControl,
  SelectFieldControl,
  SwitchFieldControl,
  TextFieldControl,
} from '@/components/ui/form-fields'
import { PlusIcon, TrashIcon } from '@/components/ui/icons'
import { PAGE_LIMIT_MAX } from '@/lib/api/common'

import {
  findMenuChoice,
  useMenuChoices,
  useRecipeModifierActions,
  useStockItems,
  useStockItemsById,
  useUnitCatalog,
} from '../hooks'
import type { MenuChoice, MenuChoiceGroup } from '../hooks'
import { StockItemType } from '../types'
import type { StockItem, UnitOfMeasure } from '../types'
import type {
  CreateRecipeModifierBody,
  Recipe,
  RecipeModifier,
  RecipeModifierComponent,
  RecipeModifierComponentInput,
  RecipeModifierMode,
  UpdateRecipeModifierBody,
} from '../types'
import { modifierModeLabels, recipeQuantity, unitsLike } from './inventory-format'

/** Component quantities take the same fourth decimal a recipe line does. */
const QUANTITY_FORMAT: Intl.NumberFormatOptions = { maximumFractionDigits: 4 }

/** What can be put in: the first page of INGREDIENT items, as large as the API allows. */
const CANDIDATE_QUERY = {
  limit: PAGE_LIMIT_MAX,
  offset: 0,
  category: StockItemType.Ingredient,
}

/**
 * Where a component's amount comes from. `BASE` is the API's `BASE_COMPONENT`:
 * the recipe's own amount for something taken out, the amount taken out for the
 * one thing a swap puts in. `FIXED` is a number typed here.
 */
type Amount = 'BASE' | 'FIXED'

/* No rule on the ingredient here: both lists always hold a row, and the one the
   mode hides would fail the form with an error nobody can see. The refinement
   below checks only the rows on screen. */
const componentSchema = z.object({
  stockItemId: z.string(),
  amount: z.enum(['BASE', 'FIXED']),
  quantity: z.number().optional(),
  unitOfMeasure: z.string().optional(),
})

type ComponentValues = z.infer<typeof componentSchema>

/**
 * The service's rules, checked before the request rather than after it, and
 * pinned to the field they are about — see `assertComposition` on the server.
 */
const schema = z
  .object({
    recipeId: z.string().min(1, 'Choose a recipe.'),
    name: z.string().trim().min(1, 'Give the modifier a name.').max(100, 'At most 100 characters.'),
    /** The linked menu choice's id, or `NOT_LINKED`. */
    menuChoiceId: z.string(),
    affectsStock: z.boolean(),
    mode: z.enum(['ADD', 'REMOVE', 'REPLACE']),
    removes: z.array(componentSchema),
    adds: z.array(componentSchema),
  })
  .superRefine((values, context) => {
    if (!values.affectsStock) return

    const removes = values.mode === 'ADD' ? [] : values.removes
    const adds = values.mode === 'REMOVE' ? [] : values.adds

    if (values.mode !== 'ADD' && removes.length === 0) {
      context.addIssue({ code: 'custom', path: ['removes'], message: 'Choose what it takes out.' })
    }
    if (values.mode !== 'REMOVE' && adds.length === 0) {
      context.addIssue({ code: 'custom', path: ['adds'], message: 'Choose what it puts in.' })
    }

    for (const [list, rows] of [
      ['removes', removes],
      ['adds', adds],
    ] as const) {
      rows.forEach((row, index) => {
        if (!row.stockItemId) {
          context.addIssue({
            code: 'custom',
            path: [list, index, 'stockItemId'],
            message: 'Choose an ingredient.',
          })
        }
        if (row.amount !== 'FIXED') return
        if (!row.quantity || row.quantity <= 0) {
          context.addIssue({
            code: 'custom',
            path: [list, index, 'quantity'],
            message: 'Enter an amount more than 0.',
          })
        }
        if (!row.unitOfMeasure) {
          context.addIssue({
            code: 'custom',
            path: [list, index, 'unitOfMeasure'],
            message: 'Choose a unit.',
          })
        }
      })

      const ids = rows.map((row) => row.stockItemId).filter(Boolean)
      if (new Set(ids).size !== ids.length) {
        context.addIssue({
          code: 'custom',
          path: [list],
          message: 'Each ingredient can only be listed once.',
        })
      }
    }

    // Only a swap's one removal at the recipe's amount has an amount to copy.
    const copying = adds.filter((row) => row.amount === 'BASE')
    if (
      copying.length > 0 &&
      !(copying.length === 1 && removes.length === 1 && removes[0].amount === 'BASE')
    ) {
      context.addIssue({
        code: 'custom',
        path: ['adds'],
        message:
          '“Same amount” copies what one ingredient taken out at the recipe’s amount used. Use a set amount instead.',
      })
    }

    if (adds.some((add) => removes.some((remove) => remove.stockItemId === add.stockItemId))) {
      context.addIssue({
        code: 'custom',
        path: ['adds'],
        message: 'An ingredient cannot be both taken out and put in.',
      })
    }
  })

type FormValues = z.infer<typeof schema>

const NEW_REMOVE: ComponentValues = { stockItemId: '', amount: 'BASE' }
const NEW_ADD: ComponentValues = { stockItemId: '', amount: 'FIXED' }

/**
 * The picker's "no link" key. Not `''`: React Aria reads an empty key as no
 * selection, which would blank the trigger instead of saying "Not linked".
 */
const NOT_LINKED = 'not-linked'

/** Lines for no recipe at all, held once so the memos that read them stay put. */
const NO_LINES: Recipe['lines'] = []

/** A saved component back as a form row. A `BASE_COMPONENT` one types no amount. */
function toRow(component: RecipeModifierComponent): ComponentValues {
  return component.quantityMode === 'FIXED'
    ? {
        stockItemId: component.stockItemId,
        amount: 'FIXED',
        quantity: component.quantity,
        unitOfMeasure: component.unitOfMeasure,
      }
    : { stockItemId: component.stockItemId, amount: 'BASE' }
}

/** The form as a modifier fills it, or empty for a new one on `recipeId`. */
function initialValues(modifier: RecipeModifier | null, recipeId: string): FormValues {
  if (!modifier) {
    return {
      recipeId,
      name: '',
      menuChoiceId: NOT_LINKED,
      affectsStock: false,
      mode: 'ADD',
      removes: [NEW_REMOVE],
      adds: [NEW_ADD],
    }
  }

  const rowsOf = (effectType: 'ADD' | 'REMOVE') =>
    modifier.components.filter((part) => part.effectType === effectType).map(toRow)
  const removes = rowsOf('REMOVE')
  const adds = rowsOf('ADD')

  return {
    recipeId: modifier.recipeId,
    name: modifier.name,
    menuChoiceId: modifier.catalogModifierRef ?? NOT_LINKED,
    affectsStock: modifier.affectsStock,
    mode: modifier.mode ?? 'ADD',
    removes: removes.length ? removes : [NEW_REMOVE],
    adds: adds.length ? adds : [NEW_ADD],
  }
}

/** The request's component, from a row and the side it sits on. */
function toComponent(
  row: ComponentValues,
  effectType: 'ADD' | 'REMOVE',
): RecipeModifierComponentInput {
  return row.amount === 'BASE'
    ? { stockItemId: row.stockItemId, effectType, quantityMode: 'BASE_COMPONENT' }
    : {
        stockItemId: row.stockItemId,
        effectType,
        quantityMode: 'FIXED',
        quantity: row.quantity as number,
        unitOfMeasure: row.unitOfMeasure as UnitOfMeasure,
      }
}

/** An ingredient as the selects offer it: its name, its unit, and a hint. */
interface IngredientOption {
  id: string
  label: string
  hint: string
  unit: UnitOfMeasure
}

interface RecipeModifierFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  /** The recipes the modifier can go on. */
  recipes: Recipe[]
  /** The recipe the form opens on. */
  defaultRecipeId: string
  /** The modifier to edit, or `null` for a new one. */
  modifier?: RecipeModifier | null
  /** Told which recipe the modifier was saved to. */
  onSaved: (recipeId: string) => void
}

/**
 * A new modifier on a recipe — a choice a customer makes, and what it does to
 * the stock a sale of that recipe uses.
 *
 * Most choices change nothing on the shelf ("no ice", "extra hot"), so stock is
 * off by default and the form is a name. Switched on, the mode decides what the
 * rest asks for: what it takes out of the recipe, what it puts in, or both for a
 * swap. What it takes out can only be the recipe's own ingredients, and by
 * default at the recipe's amount — "no milk" removes exactly the milk the recipe
 * uses. What a swap puts in can copy that amount, so oat for dairy is one choice
 * per side and no arithmetic.
 *
 * A new modifier's recipe can be changed here. What it takes out is the old
 * recipe's ingredients, so changing it starts that list over. A saved modifier
 * stays on its recipe: the service has no move.
 *
 * The menu choice is what lets a sale find it. The POS sends the id of each
 * choice picked on a line, and only a modifier linked to that choice is applied
 * — an unlinked one is kept but never used.
 */
export function RecipeModifierFormDialog({
  isOpen,
  onOpenChange,
  recipes,
  defaultRecipeId,
  modifier = null,
  onSaved,
}: RecipeModifierFormDialogProps) {
  const { create, update } = useRecipeModifierActions()
  const save = modifier ? update : create
  const isEditing = modifier !== null

  /* One idempotency key per distinct body, as on the other create forms. */
  const [attempt, setAttempt] = useState<{ key: string; body: string } | null>(null)

  const { control, handleSubmit, setValue, getValues, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: initialValues(modifier, defaultRecipeId),
  })

  const { groups: menuGroups, isLoading: isLoadingMenu } = useMenuChoices()

  const recipeId = useWatch({ control, name: 'recipeId' })
  const affectsStock = useWatch({ control, name: 'affectsStock' })
  const mode = useWatch({ control, name: 'mode' })

  const removes = useFieldArray({ control, name: 'removes' })
  const adds = useFieldArray({ control, name: 'adds' })

  const recipe = recipes.find((candidate) => candidate.id === recipeId) ?? null
  const lines = recipe?.lines ?? NO_LINES

  /* A new recipe has other ingredients to take out, so that list starts over.
     What goes in stays, but for the new recipe's own item, which cannot. */
  function changeRecipe(next: Recipe) {
    removes.replace([NEW_REMOVE])
    adds.replace(
      getValues('adds').map((row) => (row.stockItemId === next.stockItemId ? NEW_ADD : row)),
    )
  }

  /* What can be taken out: the recipe's own ingredients, nothing else. */
  const baseItems = useStockItemsById(lines.map((line) => line.itemId))
  const removeOptions = useMemo<IngredientOption[]>(
    () =>
      lines.flatMap((line) => {
        const item = baseItems.get(line.itemId)
        return item
          ? [
              {
                id: item.id,
                label: item.name,
                hint: `${recipeQuantity(line.qty)} ${line.unitOfMeasure} in the recipe`,
                unit: item.unitOfMeasure,
              },
            ]
          : []
      }),
    [lines, baseItems],
  )

  /* What can be put in: any ingredient but the recipe's own item. The list
     leaves archived items out already, which the service would refuse.

     Plus what a saved modifier already puts in, read by id: it may be an item
     of another type — packaging — from before this list was narrowed, and
     without it in the options its select would open blank. */
  const { items: candidates } = useStockItems(CANDIDATE_QUERY)
  const savedAddIds = useMemo(
    () =>
      (modifier?.components ?? [])
        .filter((part) => part.effectType === 'ADD')
        .map((part) => part.stockItemId),
    [modifier],
  )
  const savedAdds = useStockItemsById(savedAddIds)
  const addOptions = useMemo<IngredientOption[]>(
    () =>
      [
        ...new Map<string, StockItem>([
          ...[...savedAdds.values()].map((item) => [item.id, item] as const),
          ...candidates.map((item) => [item.id, item] as const),
        ]).values(),
      ]
        .filter((item) => item.id !== recipe?.stockItemId)
        .map((item) => ({
          id: item.id,
          label: item.name,
          hint: `${item.sku} · ${item.unitOfMeasure}`,
          unit: item.unitOfMeasure,
        })),
    [candidates, savedAdds, recipe],
  )

  const submit = handleSubmit(async (values) => {
    const menuChoiceId = values.menuChoiceId === NOT_LINKED ? null : values.menuChoiceId
    const stock: Pick<CreateRecipeModifierBody, 'affectsStock' | 'mode' | 'components'> = {
      affectsStock: values.affectsStock,
    }

    // Only the side the mode uses is sent; the other is whatever was left in the
    // form before the mode changed. Sent in full on every save: an edit that
    // changes the mode must send components, and one that does not and sends
    // the same ones changes nothing.
    if (values.affectsStock) {
      stock.mode = values.mode
      stock.components = [
        ...(values.mode === 'ADD' ? [] : values.removes.map((row) => toComponent(row, 'REMOVE'))),
        ...(values.mode === 'REMOVE' ? [] : values.adds.map((row) => toComponent(row, 'ADD'))),
      ]
    }

    if (modifier) {
      // `null` unlinks; the service takes it as a change only when there was a link.
      const body: UpdateRecipeModifierBody = {
        name: values.name.trim(),
        catalogModifierRef: menuChoiceId,
        ...stock,
      }
      await update.mutateAsync({ recipeId: modifier.recipeId, modifierId: modifier.id, body })
      onSaved(modifier.recipeId)
      return
    }

    const body: CreateRecipeModifierBody = {
      name: values.name.trim(),
      ...(menuChoiceId ? { catalogModifierRef: menuChoiceId } : {}),
      ...stock,
    }

    // The same body on another recipe is another modifier, so the recipe is part of the key.
    const serialised = JSON.stringify({ recipeId: values.recipeId, body })
    const next =
      attempt?.body === serialised ? attempt : { key: crypto.randomUUID(), body: serialised }
    setAttempt(next)

    await create.mutateAsync({ recipeId: values.recipeId, body, idempotencyKey: next.key })
    onSaved(values.recipeId)
  })

  /* A link the menu no longer lists — its choice was deleted, or the ref was
     typed by hand before there was a picker — stays selectable, so opening the
     form does not quietly drop it. */
  const savedRef = modifier?.catalogModifierRef ?? null
  const isSavedRefMissing =
    savedRef !== null && !isLoadingMenu && findMenuChoice(menuGroups, savedRef) === null

  const takesOut = affectsStock && mode !== 'ADD'
  const putsIn = affectsStock && mode !== 'REMOVE'

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={isEditing ? 'Edit modifier' : 'New modifier'}
      description={`A choice on ${recipe?.name ?? 'a recipe'}, and what it does to the stock a sale uses.`}
      submitLabel={isEditing ? 'Save changes' : 'Add modifier'}
      onSubmit={submit}
      isBusy={save.isPending}
      error={save.error}
      errorFallback={isEditing ? 'Could not save this modifier.' : 'Could not add this modifier.'}
      dialogClassName="max-w-4xl"
      scroll="inside"
    >
      <FormSection title="The modifier">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextFieldControl
            control={control}
            name="name"
            label="Name"
            placeholder="Oat milk, Extra shot…"
            isRequired
            autoFocus
          />
          <Controller
            control={control}
            name="recipeId"
            render={({ field, fieldState }) => (
              <Select
                selectedKey={field.value || null}
                onSelectionChange={(key) => {
                  const choice = recipes.find((candidate) => candidate.id === String(key))
                  if (!choice || choice.id === field.value) return
                  field.onChange(choice.id)
                  changeRecipe(choice)
                }}
                onBlur={field.onBlur}
                isInvalid={fieldState.invalid}
                isRequired
                isDisabled={isEditing}
                fullWidth
                className="flex flex-col gap-1.5"
              >
                <Label>Recipe</Label>
                <Select.Trigger ref={field.ref}>
                  <Select.Value className="truncate">
                    {({ selectedText }) =>
                      selectedText || <span className="text-muted">Choose…</span>
                    }
                  </Select.Value>
                  <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                  <ListBox>
                    {recipes.map((option) => (
                      <ListBoxItem key={option.id} id={option.id} textValue={option.name}>
                        {option.name}
                      </ListBoxItem>
                    ))}
                  </ListBox>
                </Select.Popover>
                <FieldError>{fieldState.error?.message}</FieldError>
              </Select>
            )}
          />
          <div className="sm:col-span-2">
            <MenuChoiceField
              control={control}
              groups={menuGroups}
              isLoading={isLoadingMenu}
              missingRef={isSavedRefMissing ? savedRef : null}
              onChoose={(choice) => {
                // A blank name takes the choice's own, which is what it is called on the menu.
                if (!getValues('name').trim()) {
                  setValue('name', choice.name, { shouldValidate: true })
                }
              }}
            />
          </div>
        </div>
      </FormSection>

      <FormSection title="Stock">
        <div className="flex flex-col gap-4">
          <SwitchFieldControl
            control={control}
            name="affectsStock"
            label="Changes the stock a sale uses"
            description="Leave off for a choice that changes nothing on the shelf — no ice, extra hot."
          />

          {affectsStock && (
            <SelectFieldControl
              control={control}
              name="mode"
              label="What it does"
              isRequired
              options={(Object.keys(modifierModeLabels) as RecipeModifierMode[]).map((value) => ({
                value,
                label: modifierModeLabels[value],
              }))}
            />
          )}
        </div>
      </FormSection>

      {takesOut && (
        <FormSection
          title="Takes out"
          description="Only the recipe's own ingredients can be taken out."
          action={
            removeOptions.length > 0 && (
              <AddRowButton label="Add ingredient" onPress={() => removes.append(NEW_REMOVE)} />
            )
          }
          alert={
            <ListError
              message={
                // With nothing to choose there is no row on screen to hold the
                // error, so it is said here or not at all.
                removeOptions.length === 0 && formState.errors.removes
                  ? 'Nothing to take out: this recipe has no ingredients yet.'
                  : listError(formState.errors.removes)
              }
            />
          }
        >
          {removeOptions.length === 0 ? (
            <FormSectionEmpty>
              This recipe has no ingredients yet, so there is nothing to take out. Add them to the
              recipe first.
            </FormSectionEmpty>
          ) : (
            <div className="flex flex-col gap-3">
              {removes.fields.map((field, index) => (
                <ComponentRow
                  key={field.id}
                  list="removes"
                  index={index}
                  control={control}
                  setValue={setValue}
                  options={removeOptions}
                  amountOptions={[
                    { value: 'BASE', label: "The recipe's amount" },
                    { value: 'FIXED', label: 'A set amount' },
                  ]}
                  canRemove={removes.fields.length > 1}
                  onRemove={() => removes.remove(index)}
                />
              ))}
            </div>
          )}
        </FormSection>
      )}

      {putsIn && (
        <FormSection
          title="Puts in"
          description={
            mode === 'REPLACE'
              ? 'What goes in instead. "Same amount" uses exactly what was taken out.'
              : 'What the choice adds to the recipe.'
          }
          action={<AddRowButton label="Add ingredient" onPress={() => adds.append(NEW_ADD)} />}
          alert={<ListError message={listError(formState.errors.adds)} />}
        >
          <div className="flex flex-col gap-3">
            {adds.fields.map((field, index) => (
              <ComponentRow
                key={field.id}
                list="adds"
                index={index}
                control={control}
                setValue={setValue}
                options={addOptions}
                amountOptions={[
                  { value: 'FIXED', label: 'A set amount' },
                  ...(mode === 'REPLACE'
                    ? [{ value: 'BASE' as const, label: 'Same amount as taken out' }]
                    : []),
                ]}
                canRemove={adds.fields.length > 1}
                onRemove={() => adds.remove(index)}
              />
            ))}
          </div>
        </FormSection>
      )}
    </FormModal>
  )
}

interface MenuChoiceFieldProps {
  control: Control<FormValues>
  groups: MenuChoiceGroup[]
  isLoading: boolean
  /** A saved link the menu no longer lists, kept as an option so it is not lost. */
  missingRef: string | null
  onChoose: (choice: MenuChoice) => void
}

/**
 * The menu choice a modifier stands for, picked from the menu's choice groups.
 * "Not linked" is a real answer — a modifier can be set up before its choice —
 * but the description says what it costs.
 */
function MenuChoiceField({
  control,
  groups,
  isLoading,
  missingRef,
  onChoose,
}: MenuChoiceFieldProps) {
  return (
    <Controller
      control={control}
      name="menuChoiceId"
      render={({ field }) => (
        <Select
          selectedKey={field.value}
          onSelectionChange={(key) => {
            if (key === null) return
            const id = String(key)
            field.onChange(id)
            const found = findMenuChoice(groups, id)
            if (found) onChoose(found.choice)
          }}
          onBlur={field.onBlur}
          isDisabled={isLoading}
          fullWidth
          className="flex flex-col gap-1.5"
        >
          <Label>Menu choice</Label>
          <Select.Trigger ref={field.ref}>
            <Select.Value className="truncate">
              {({ selectedText }) =>
                isLoading ? (
                  <span className="text-muted">Loading the menu…</span>
                ) : (
                  selectedText || <span className="text-muted">Not linked</span>
                )
              }
            </Select.Value>
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              <ListBox.Section id="none">
                <ListBoxItem id={NOT_LINKED} textValue="Not linked">
                  <span className="text-muted">Not linked</span>
                </ListBoxItem>
                {missingRef && (
                  <ListBoxItem id={missingRef} textValue={`No longer on the menu (${missingRef})`}>
                    No longer on the menu ({missingRef})
                  </ListBoxItem>
                )}
              </ListBox.Section>
              {groups.map((group) => (
                <ListBox.Section key={group.id} id={group.id}>
                  <Header>{group.name}</Header>
                  {group.choices.map((choice) => (
                    <ListBoxItem
                      key={choice.id}
                      id={choice.id}
                      textValue={`${group.name}: ${choice.name}`}
                    >
                      {choice.name}
                    </ListBoxItem>
                  ))}
                </ListBox.Section>
              ))}
            </ListBox>
          </Select.Popover>
          <Description>
            The choice on the menu this modifier stands for. A sale only applies a modifier that is
            linked to the choice the customer picked.
          </Description>
        </Select>
      )}
    />
  )
}

/** A list-level error — wrong count, a repeat — wherever react-hook-form put it. */
function listError(
  error: { message?: string; root?: { message?: string } } | undefined,
): string | undefined {
  return error?.root?.message ?? error?.message
}

function ListError({ message }: { message?: string }) {
  if (!message) return null

  return (
    <p role="alert" className="text-danger mt-2 text-xs">
      {message}
    </p>
  )
}

function AddRowButton({ label, onPress }: { label: string; onPress: () => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="border-border h-8 rounded-full border px-3 text-xs"
      onPress={onPress}
    >
      <PlusIcon width={14} height={14} />
      {label}
    </Button>
  )
}

interface ComponentRowProps {
  list: 'removes' | 'adds'
  index: number
  control: Control<FormValues>
  setValue: UseFormSetValue<FormValues>
  options: IngredientOption[]
  amountOptions: { value: Amount; label: string }[]
  canRemove: boolean
  onRemove: () => void
}

/**
 * One ingredient a modifier touches: which, and how much.
 *
 * Choosing the ingredient resets a set amount's unit to the ingredient's own —
 * the one unit certain to pass — and the unit list only offers units that
 * measure the same thing, so the form cannot build a component the API would
 * refuse.
 */
function ComponentRow({
  list,
  index,
  control,
  setValue,
  options,
  amountOptions,
  canRemove,
  onRemove,
}: ComponentRowProps) {
  const ingredientId = useWatch({ control, name: `${list}.${index}.stockItemId` as const })
  const amount = useWatch({ control, name: `${list}.${index}.amount` as const })
  const ingredientUnit = options.find((option) => option.id === ingredientId)?.unit
  const { catalog: units } = useUnitCatalog()

  /* An amount this side no longer offers — a swap's "same amount", after the mode
     moved off REPLACE — falls back to the first one it does. */
  const offered = amountOptions.some((option) => option.value === amount)
  useEffect(() => {
    if (!offered) setValue(`${list}.${index}.amount`, amountOptions[0].value)
  }, [offered, list, index, amountOptions, setValue])

  return (
    /* The quantity column holds the figure between its −/+ (40px each), so it is
       as wide as the recipe editor's; `minmax(0, …)` lets a long ingredient name
       truncate instead of pushing the row wider. */
    <div className="border-border grid gap-3 rounded-xl border p-3 sm:grid-cols-[minmax(0,1fr)_180px] lg:grid-cols-[minmax(0,1fr)_170px_10rem_7rem_2.75rem]">
      <Controller
        control={control}
        name={`${list}.${index}.stockItemId` as const}
        render={({ field, fieldState }) => (
          <Select
            selectedKey={field.value || null}
            onSelectionChange={(key) => {
              const choice = options.find((option) => option.id === String(key))
              field.onChange(choice?.id ?? '')
              if (choice) {
                setValue(`${list}.${index}.unitOfMeasure`, choice.unit, { shouldValidate: true })
              }
            }}
            onBlur={field.onBlur}
            isInvalid={fieldState.invalid}
            isRequired
            fullWidth
            className="flex flex-col gap-1.5"
          >
            <Label>Ingredient</Label>
            <Select.Trigger ref={field.ref}>
              <Select.Value className="truncate">
                {({ selectedText }) => selectedText || <span className="text-muted">Choose…</span>}
              </Select.Value>
              <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
              <ListBox>
                {options.map((option) => (
                  <ListBoxItem key={option.id} id={option.id} textValue={option.label}>
                    <span className="flex flex-col">
                      <span>{option.label}</span>
                      <span className="text-muted text-xs">{option.hint}</span>
                    </span>
                  </ListBoxItem>
                ))}
              </ListBox>
            </Select.Popover>
            <FieldError>{fieldState.error?.message}</FieldError>
          </Select>
        )}
      />

      <SelectFieldControl
        control={control}
        name={`${list}.${index}.amount` as const}
        label="Amount"
        isRequired
        options={amountOptions}
      />

      {amount === 'FIXED' ? (
        <>
          <NumberFieldControl
            control={control}
            name={`${list}.${index}.quantity` as const}
            label="Quantity"
            minValue={0}
            formatOptions={QUANTITY_FORMAT}
            isRequired
          />
          <SelectFieldControl
            control={control}
            name={`${list}.${index}.unitOfMeasure` as const}
            label="Unit"
            isRequired
            isDisabled={!ingredientUnit}
            options={(ingredientUnit ? unitsLike(ingredientUnit, units) : []).map((unit) => ({
              value: unit,
              label: unit,
            }))}
          />
        </>
      ) : (
        /* Two empty cells, so the remove button keeps its column on wide screens. */
        <span className="hidden lg:col-span-2 lg:block" />
      )}

      <Button
        variant="ghost"
        aria-label="Remove ingredient"
        isDisabled={!canRemove}
        onPress={onRemove}
        className="text-muted hover:text-danger size-11 self-end rounded-full"
      >
        <TrashIcon width={16} height={16} />
      </Button>
    </div>
  )
}
